import React, { FC, ReactElement, useCallback, useContext } from 'react'
import { RealTimeBotsStatus } from '../Types'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { LanguageContext, PromptContext, UserInfoContext } from '../../Common/Contexts'
import FormRow from '../../Common/Components/FormRow'
import { LanguageCME } from '../../../../i18n'
import { UserInfoContextType } from '../../Common/Contexts/UserInfoContext'
import { RECURSIVE_BOTS_ENABLE_FIELD_NAME } from '../FieldName'
import { isDevOpsAdmin } from '../../Common/Utils/AuthorisationUtils'
import { useFormContext, UseFormMethods } from '../../../Vendor/react-hook-form/dist/index.ie11'
import { displayError } from '../../Common/Utils/Messages'
import { PromptDisplayType } from '../../Common/Contexts/PromptContext'
import { requestJSON } from '../../../NonReact/common/API/REST/RESTClient'
import { cmeCallCaptureControlURL } from '../../../NonReact/constants/ApiConstants'
import { RESTMethods } from '../../../NonReact/constants/REST'
import { RouterCheckbox } from '../../../ReactRouting/Components/Atoms/Inputs/RouterCheckbox'
import { RoundedButton } from '../../../ReactRouting/Components/Atoms/RoundedButton/RoundedButton'

type Props = {
    isRootCME: boolean
    botStatus: Nullable<RealTimeBotsStatus>
    selectedCMEID: string
    reloadCMEData: () => void
}

export const Advanced: FC<Props> = ({ isRootCME, botStatus, selectedCMEID, reloadCMEData }: Props): ReactElement => {
    const { displayPromptPromise }: PromptDisplayType = useContext(PromptContext)
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    const userInfo: UserInfoContextType = useContext(UserInfoContext)

    const { watch }: UseFormMethods = useFormContext()

    const recursiveBotsEnabledValue: boolean = watch(RECURSIVE_BOTS_ENABLE_FIELD_NAME)

    const enableBots = useCallback((): void => {
        displayPromptPromise(language.get(LanguageCME.CME_EnableVoiceAppBots_Confirmation_Title)).then((): void => {
            requestJSON(cmeCallCaptureControlURL(selectedCMEID), {
                method: RESTMethods.PUT,
                body: { BotsDisabled: false, Cascade: recursiveBotsEnabledValue }
            })
                .then(reloadCMEData)
                .catch(displayError)
        })
    }, [recursiveBotsEnabledValue])

    const disableBots = useCallback((): void => {
        displayPromptPromise(language.get(LanguageCME.CME_EnableVoiceAppBots_Confirmation_Title)).then((): void => {
            requestJSON(cmeCallCaptureControlURL(selectedCMEID), {
                method: RESTMethods.PUT,
                body: { BotsDisabled: true, Cascade: recursiveBotsEnabledValue }
            })
                .then(reloadCMEData)
                .catch(displayError)
        })
    }, [recursiveBotsEnabledValue])

    return (
        <div className='cme-advanced-tab tab-area'>
            {isDevOpsAdmin(userInfo) && isRootCME ? (
                <FormRow>
                    <RouterCheckbox
                        labelLeft={language.get(LanguageCME.CME_Fields_RecursiveRealTimeBots)}
                        name={RECURSIVE_BOTS_ENABLE_FIELD_NAME}
                    />
                </FormRow>
            ) : (
                <></>
            )}
            <FormRow>
                <RoundedButton isOutlined onClick={enableBots} type='button'>
                    {language.getInterpolated(
                        recursiveBotsEnabledValue
                            ? LanguageCME.CME_EnableVoiceAppBots_Recursive
                            : LanguageCME.CME_EnableVoiceAppBots,
                        botStatus?.disabledTelSysCount
                    )}
                </RoundedButton>
            </FormRow>
            <FormRow>
                <RoundedButton isOutlined onClick={disableBots} type='button'>
                    {language.getInterpolated(
                        recursiveBotsEnabledValue
                            ? LanguageCME.CME_DisableVoiceAppBots_Recursive
                            : LanguageCME.CME_DisableVoiceAppBots,
                        botStatus?.enabledTelSysCount
                    )}
                </RoundedButton>
            </FormRow>
        </div>
    )
}
